import {
    Button,
    Grid,
    TextField,
    Typography,
    Box,
    IconButton,
    Autocomplete,
    Chip,
    Checkbox,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Formik } from "formik";
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Loadable } from "../../../common";
import { ContractsSearchComponent } from "../../contractsPage/ContractsSearchComponent";
import { Portlet, PortletBody, PortletHeader } from "../../../partials/content/Portlet";
import Helmet from "react-helmet";
import * as SUPPLEMENTARY_KNOWLEDGE_API from "../../../store/api/supplementaryKnowledge";
import { getDocument } from "../../../store/api/contracts";
import { SERVER_URL } from "../../../store/constants/api";

const useStyles = makeStyles(theme => ({
    textField: {
        width: "100%"
    },
    uploadButton: {
        marginTop: theme.spacing(2),
    },
    previewButton: {
        marginTop: theme.spacing(2),
        marginLeft: theme.spacing(2),
    },
    documentPreviewBox: {
        minHeight: 200,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.grey[50],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(3),
        marginBottom: theme.spacing(2),
    },
    documentPreviewSection: {
        marginTop: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.shape.borderRadius,
    },
}));

interface SupplementaryKnowledgeEditPageProps {
    intl: any;
    history: any;
    uploadPending: boolean;
    tinyContracts: any[];
    actions: {
        getTinyContracts: () => void;
        resetSelectedContracts: () => void;
        setSelectedContracts: (ids: string[]) => void;
        uploadDocument: (payload: FormData, callback: () => void) => void;
    };
}

export default function SupplementaryKnowledgeEditPage(props: SupplementaryKnowledgeEditPageProps) {
    const {
        intl,
        history,
        uploadPending,
        tinyContracts,
        actions: { getTinyContracts, resetSelectedContracts, setSelectedContracts, uploadDocument }
    } = props;
    
    const { id } = useParams<{ id?: string }>();
    const isEditMode = !!id;
    const classes = useStyles();
    const [file, setFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(isEditMode);
    const [documentData, setDocumentData] = useState<any>(null);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Fetch document data if in edit mode
    useEffect(() => {
        if (isEditMode && id) {
            setLoading(true);
            SUPPLEMENTARY_KNOWLEDGE_API.getSuppKbDocument(parseInt(id))
                .then((response: any) => {
                    setDocumentData(response);
                    // Set selected contracts
                    const contractIds = response.suppKBDocumentMetadataContractMap?.map(
                        (item: any) => item.contract.id.toString()
                    ) || [];
                    setSelectedContracts(contractIds);
                })
                .catch((error: any) => {
                    console.error("Error fetching document:", error);
                    toast.error("Failed to load document data", {
                        autoClose: 5000,
                    });
                    history.goBack();
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [isEditMode, id]);

    useEffect(() => {
        getTinyContracts();
        return () => {
            resetSelectedContracts();
        };
    }, [getTinyContracts]);

    const handleBack = () => {
        history.goBack();
    };

    // Helper function to extract filename from S3 key
    const getFileNameFromS3Key = (s3Key: string): string => {
        if (!s3Key) return '';
        // Extract the part after the last slash
        const parts = s3Key.split('/');
        const fileNameWithUUID = parts[parts.length - 1];
        // Remove UUID prefix (format: UUID_filename.ext)
        const underscoreIndex = fileNameWithUUID.indexOf('_');
        if (underscoreIndex !== -1) {
            return fileNameWithUUID.substring(underscoreIndex + 1);
        }
        return fileNameWithUUID;
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            const selectedFile = event.target.files[0];
            setFile(selectedFile);
            
            // Create preview URL for PDF
            if (selectedFile.type === 'application/pdf') {
                setPreviewUrl(URL.createObjectURL(selectedFile));
            } else {
                setPreviewUrl(null);
            }
        }
    };

    const handlePreview = () => {
        if (previewUrl) {
            // Preview newly uploaded file
            window.open(previewUrl, '_blank');
        } else if (isEditMode && documentData?.documentMetadata?.s3Key) {
            // Download existing document in edit mode
            handleDownloadDocument();
        }
    };

    const handleDownloadDocument = () => {
        if (!documentData?.documentMetadataId || !documentData?.documentMetadata) return;
        
        // Helper function to download and open the document
        const download = (file, documentName, metadata) => {
            let documentType = '';
            if (metadata) {
                const md = JSON.parse(metadata);
                documentType = md.type;
            }
            const myNewFile = new File([file], documentName, { type: documentType });
            const browserWindow = window.open();
            if (browserWindow) {
                browserWindow.location.href = window.URL.createObjectURL(myNewFile);
            }
        };
        
        // Use the existing getDocument service from contracts API
        const documentName = documentData.documentMetadata.documentName;
        const metadata = documentData.documentMetadata.metadata;
        
        getDocument(documentData.documentMetadataId)
            .then((blob) => {
                download(blob, documentName, metadata);
            })
            .catch((error) => {
                toast.error('Failed to load document', {
                    autoClose: 5000,
                });
            });
    };

    // Get contract objects for initial values in edit mode
    const getInitialContracts = () => {
        if (!isEditMode || !documentData?.suppKBDocumentMetadataContractMap) {
            return [];
        }
        
        const contractIds = documentData.suppKBDocumentMetadataContractMap.map(
            (item: any) => item.contract.id
        );
        
        return tinyContracts.filter((contract: any) => 
            contractIds.includes(contract.id)
        );
    };

    const initialValues = {
        documentName: documentData?.documentMetadata?.documentName || '',
        description: documentData?.documentMetadata?.description || '',
        contracts: getInitialContracts()
    };

    return (
        <>
            <Helmet title={`${isEditMode ? 'Edit' : 'Add'} Supplementary Knowledge Document`} />
            <Portlet>
            <PortletHeader
                toolbar={
                        <Box display="flex" justifyContent="flex-end" alignItems="center" py={2}>
                            <Box display="flex" gap={1}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={isSubmitting}
                                    onClick={() => {
                                        // Mark that submit has been attempted
                                        setSubmitAttempted(true);
                                        if (formRef.current) {
                                            formRef.current.dispatchEvent(
                                                new Event('submit', { cancelable: true, bubbles: true })
                                            );
                                        }
                                    }}
                                >
                                    {isSubmitting ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                    disabled={isSubmitting}
                                    variant="contained"
                                    onClick={handleBack}
                                >
                                    Back
                                </Button>
                            </Box>
                        </Box>
                }
                title={isEditMode ? 'Edit Document' : 'Add Document'}
            />
                <PortletBody>
                    <Loadable loading={uploadPending || loading}>
                        <Formik
                            enableReinitialize={true}
                            initialValues={initialValues}
                            validate={values => {
                                const errors: any = {};

                                // Document name validation
                                if (!values.documentName || !values.documentName.trim()) {
                                    errors.documentName = "Document name is required";
                                }

                                // Description validation - required in both modes
                                if (!values.description || !values.description.trim()) {
                                    errors.description = "Description is required";
                                } else if (values.description.length > 500) {
                                    errors.description = "Description cannot exceed 500 characters";
                                }

                                // Contracts validation
                                if (values.contracts.length === 0) {
                                    errors.contracts = "Select at least one contract";
                                }

                                // File validation
                                if (!isEditMode && !file) {
                                    errors.file = "Please select a file";
                                } else if (file) {
                                    const allowedTypes = [
                                        "application/pdf",
                                        "application/msword",
                                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                    ];
                                    const allowedExtensions = [".pdf", ".doc", ".docx"];
                                    const fileName = file.name?.toLowerCase() || "";
                                    const fileType = file.type;

                                    const hasValidExtension = allowedExtensions.some(ext =>
                                        fileName.endsWith(ext)
                                    );
                                    const hasValidType = allowedTypes.includes(fileType);

                                    if (!hasValidExtension || !hasValidType) {
                                        errors.file = "Only PDF and Word (.doc, .docx) files are allowed";
                                    }
                                }

                                return errors;
                            }}
                            onSubmit={async (values, { setSubmitting }) => {
                                setIsSubmitting(true);
                                
                                try {
                                    let response: any;
                                    
                                    if (isEditMode) {
                                        // Edit mode - send JSON payload
                                        const payload = {
                                            id: documentData?.id,
                                            contractIds: values.contracts.map((contract: any) => contract.id),
                                            documentName: values.documentName,
                                            description: values.description || '',
                                            tagIds: [],
                                            criteriaIds: []
                                        };
                                        
                                        response = await SUPPLEMENTARY_KNOWLEDGE_API.updateSuppKbDocument(payload);
                                        toast.success("Document updated successfully",{
                                            autoClose: 5000,
                                        });
                                    } else {
                                        // Add mode - send FormData
                                        const formData = new FormData();
                                        
                                        values.contracts.forEach((contract: any) => {
                                            formData.append('ContractIds', contract.id.toString());
                                        });
                                        
                                        formData.append('DocumentName', values.documentName);
                                        formData.append('Description', values.description || '');
                                        
                                        // Add file and metadata
                                        if (file) {
                                            const metadata = {
                                                type: file.type,
                                            };
                                            formData.append('metadata', JSON.stringify(metadata));
                                            formData.append('Document', file);
                                        }
                                        
                                        // First API - Upload document
                                        // Upload document - backend now returns s3_key in response
                                        response = await SUPPLEMENTARY_KNOWLEDGE_API.uploadSuppKbDocument(formData);
                                        
                                        // Extract s3_key from upload response (backend now provides it)
                                        // Handle multiple possible response structures
                                        const s3Key = response?.s3_key || 
                                                      response?.s3Key || 
                                                      response?.documentMetadata?.s3_key ||
                                                      response?.documentMetadata?.s3Key ||
                                                      '';
                                        
                                        // Second API - Create supplementary document in AI API
                                        const aiPayload = {
                                            s3_key: s3Key,
                                            filename: values.documentName,
                                            contract_ids: values.contracts.map((contract: any) => contract.id),
                                            document_id: response.documentMetadataId,
                                            metadata: { type: file?.type || 'application/pdf' },
                                            description: values.description || '',
                                        };
                                        
                                        const aiResponse = await SUPPLEMENTARY_KNOWLEDGE_API.createSupplementaryDocument(aiPayload);
                                        
                                        toast.success("Document uploaded successfully",{
                                            autoClose: 5000,
                                        });
                                    }
                                    
                                    // Check if response indicates completion and redirect
                                    // Assuming response has a status field or is successful if no error thrown
                                    const isComplete = response?.status === 'completed' || 
                                                      response?.status === 'success' || 
                                                      response?.status === 'Completed' ||
                                                      !response?.status; // If no status field, assume success
                                    
                                    if (isComplete) {
                                        // Redirect to knowledge base page after successful operation
                                        history.push('/cms/iq-knowledge-base');
                                    } else {
                                        // Show message that document is processing
                                        toast.info(`Document ${isEditMode ? 'updated' : 'uploaded'} and is being processed`);
                                        // Still redirect to list page
                                        history.push('/cms/iq-knowledge-base');
                                    }
                                } catch (error: any) {
                                    console.error("Error saving document:", error);
                                    // Extract error message from backend response
                                    const errorMessage = error?.payload?.errorDetails || 
                                                        error?.response?.data?.message || 
                                                        error?.response?.data?.error || 
                                                        error?.message || 
                                                        `Failed to ${isEditMode ? 'update' : 'upload'} document`;
                                    toast.error(errorMessage,{
                                        autoClose: 5000,
                                    });
                                } finally {
                                    setSubmitting(false);
                                    setIsSubmitting(false);
                                }
                            }}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleChange,
                                handleBlur,
                                handleSubmit,
                                isSubmitting,
                                setFieldValue,
                            }) => {
                                useEffect(() => {
                                    return () => {
                                        resetSelectedContracts();
                                    };
                                }, []);

                                return (
                                    <form ref={formRef} onSubmit={handleSubmit} noValidate autoComplete="off" className="supplementary-knowledge-form">
                                        <Grid container spacing={3}>

                                            {/* Document Name Field */}
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    label="Document Name"
                                                    name="documentName"
                                                    className={classes.textField}
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    value={values.documentName}
                                                    required
                                                    disabled={isEditMode}
                                                    InputProps={{
                                                        readOnly: isEditMode,
                                                    }}
                                                    helperText={
                                                        (touched.documentName && errors.documentName) ||
                                                        (isEditMode ? "Document name cannot be changed in edit mode" : "")
                                                    }
                                                    error={Boolean(
                                                        touched.documentName && errors.documentName
                                                    )}
                                                    fullWidth
                                                />
                                            </Grid>

                                            {/* Description */}
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    label="Description"
                                                    name="description"
                                                    className={classes.textField}
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    value={values.description}
                                                    required
                                                    helperText={
                                                        (touched.description && errors.description) ||
                                                        ""
                                                    }
                                                    error={Boolean(
                                                        touched.description && errors.description
                                                    )}
                                                    fullWidth
                                                />
                                            </Grid>

                                            {/* Contracts */}
                                            <Grid item xs={12} md={6} className="contract-selection">
                                                <Box sx={{ width: '100%' }}>
                                                    <ContractsSearchComponent
                                                        isMulti={true}
                                                        handleSetContract={(selected) =>
                                                            setFieldValue(
                                                                "contracts",
                                                                [...tinyContracts]
                                                                    .filter((item) => selected?.includes(String(item?.id)))
                                                                    .sort(
                                                                        (a, b) =>
                                                                            selected.indexOf(String(a?.id)) - selected.indexOf(String(b?.id))
                                                                    )
                                                            )
                                                        }
                                                        icon={
                                                            <Autocomplete
                                                                onKeyDown={(e) => { e.preventDefault(); }}
                                                                onChange={(obj, updatedContracts, event) => {
                                                                    if (event === "clear") {
                                                                        setFieldValue("contracts", [])
                                                                        resetSelectedContracts()
                                                                    }
                                                                    if (event === "removeOption") {
                                                                        setFieldValue("contracts", updatedContracts)
                                                                        setSelectedContracts(updatedContracts?.map((item: any) => String(item?.id)))
                                                                    }
                                                                }}
                                                                open={false}
                                                                value={values.contracts}
                                                                multiple
                                                                options={tinyContracts}
                                                                disableCloseOnSelect
                                                                getOptionLabel={(option: any) => option.name}
                                                                sx={{ width: '100%' }}
                                                                renderOption={(props: any, option: any, { selected }) => (
                                                                    <li {...props}>
                                                                        <Checkbox
                                                                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                                            style={{ marginRight: 8 }}
                                                                            checked={selected}
                                                                        />
                                                                        {option?.name}
                                                                    </li>
                                                                )}
                                                                renderTags={(tagValue, getTagProps) => {
                                                                    return (
                                                                        <Typography variant="caption">
                                                                            {tagValue?.map((option: any, index: number) => (
                                                                                <Chip
                                                                                    size="small"
                                                                                    color="secondary"
                                                                                    key={index}
                                                                                    label={option.name}
                                                                                    {...getTagProps({ index })}
                                                                                />
                                                                            ))}
                                                                        </Typography>
                                                                    )
                                                                }}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Contracts"
                                                                        required
                                                                        fullWidth
                                                                        error={submitAttempted && Boolean(errors.contracts)}
                                                                        helperText={submitAttempted && errors.contracts}
                                                                    />
                                                                )}
                                                            />
                                                        }
                                                    />
                                                </Box>
                                            </Grid>

                                            {/* Document Preview Section */}
                                            <Grid item xs={12}>
                                                <Box className={classes.documentPreviewSection}>
                                                    <Typography variant="subtitle1" gutterBottom sx={{ color: '#6c757d', mb: 2 }}>
                                                        Document Preview
                                                    </Typography>
                                                    
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        gap: 2,
                                                        borderBottom: file || (isEditMode && documentData?.documentMetadata?.documentName) ? '1px solid #dee2e6' : 'none',
                                                        pb: 2,
                                                        mb: 2
                                                    }}>
                                                        <input
                                                            type="file"
                                                            ref={fileInputRef}
                                                            onChange={handleFileChange}
                                                            style={{ display: 'none' }}
                                                            accept=".pdf,.doc,.docx"
                                                            disabled={isEditMode}
                                                        />

                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            startIcon={<CloudUploadIcon />}
                                                            onClick={() => fileInputRef.current?.click()}
                                                            disabled={isEditMode}
                                                            sx={{ minWidth: '120px' }}
                                                        >
                                                            UPLOAD
                                                        </Button>

                                                        <Box sx={{ 
                                                            flexGrow: 1, 
                                                            borderRight: '1px solid #dee2e6',
                                                            pr: 2,
                                                            minHeight: '40px',
                                                            display: 'flex',
                                                            alignItems: 'center'
                                                        }}>
                                                            {file ? (
                                                                <Typography variant="body2" color="textPrimary">
                                                                    {file.name}
                                                                </Typography>
                                                            ) : isEditMode && documentData?.documentMetadata?.s3Key ? (
                                                                <Typography variant="body2" color="textSecondary">
                                                                    {getFileNameFromS3Key(documentData.documentMetadata.s3Key)}
                                                                </Typography>
                                                            ) : (
                                                                <Typography variant="body2" color="textSecondary">
                                                                    
                                                                </Typography>
                                                            )}
                                                        </Box>

                                                        <Button
                                                            variant="outlined"
                                                            onClick={handlePreview}
                                                            disabled={!previewUrl && !(isEditMode && documentData?.documentMetadata?.s3Key)}
                                                            startIcon={<span style={{ fontSize: '16px' }}>🔍</span>}
                                                            sx={{ minWidth: '120px' }}
                                                        >
                                                            {isEditMode && !file ? 'View' : 'Preview'}
                                                        </Button>
                                                    </Box>

                                                    {isEditMode && (
                                                        <Typography variant="caption" color="textSecondary" display="block" sx={{ fontStyle: 'italic' }}>
                                                            File cannot be replaced in edit mode
                                                        </Typography>
                                                    )}

                                                    {!file && submitAttempted && (errors as any).file && (
                                                        <Typography variant="caption" color="error" display="block" mt={1}>
                                                            {(errors as any).file}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </form>
                                )
                            }}
                        </Formik>
                    </Loadable>
                </PortletBody>
            </Portlet>
        </>
    );
}
